

// Probably need an ID gotten off the route, useParams mayhaps?

import { Box, MenuItem, Paper, styled, TextField, useTheme } from "@mui/material";
import { gridClasses, GridColDef, GridRenderEditCellParams, useGridApiContext } from "@mui/x-data-grid";
import { GetFieldLookup, GetFieldLookupItem, ILookup, useFormMetadata, useFormSave, useLookups, useSnackbar } from "@ngt/forms";
import { FC, useCallback, useContext, useMemo } from "react";
import { Outlet, useParams } from "react-router-dom";
import { FormDefinitions, ProjectPrecinct } from "../api/dtos";
import { StyledButton } from "../components/StyledButton";
import CollapsibleTablePremium from "../components/tables/CollapsibleTablePremium";
import TitleBox from "../components/TitleBox";
import { createCustomToolbarProps } from "../components/utility/CustomToolbarProps";
import ProjectContext from "../contexts/data/ProjectContext";
import useProjectPrecinctsByProjectId from "../hooks/data/useProjectPrecinctsByProjectId";

const StyledPaper = styled(Paper) (({theme}) => ({
    marginBottom: theme.spacing(2)
}));

interface GridLookupParams extends GridRenderEditCellParams {
    lookups?: ILookup[]
}

const DataGridSelect:FC<GridLookupParams> = (props) => {
    const theme = useTheme();

    const apiRef = useGridApiContext();

    const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = +event.target.value;
        apiRef.current.setEditCellValue({ id: props.id, field: props.field, value: newValue });
    };

    return (
        <TextField
                select
                value={props.value}
                fullWidth
                variant="standard"
                slotProps={{
                    input: {
                        disableUnderline: true
                    }
                }}
                sx={{
                    alignSelf: 'center',
                    flex: '1 1 auto',
                    justifyContent: 'stretch',
                }}
                onChange={handleValueChange}
            >
                {GetFieldLookup(props.lookups, "PrecinctApproval")?.items?.map((x, i) => (
                    <MenuItem 
                        key={i.toString()} 
                        value={x.id?.toString() ?? ""}
                        sx={{
                            paddingLeft: theme.spacing(1)
                        }}
                    >
                        {x.value?.toString()}
                    </MenuItem>
                ))}
        </TextField>
    );
}

const useProjectPrecinctColumns = () => {

    const metadata = useFormMetadata(FormDefinitions.ProjectPrecinct);

    const {data: lookups, loading: _lookupsLoading} = useLookups(metadata);

    const projectPrecinctColumns = useMemo(() => {
        const columns: GridColDef<ProjectPrecinct>[] =  [
            {
                field: "name",
                headerName: "Name",
                editable: true,
                flex: 1
            },
            {
                field: "sageCode",
                headerName: "Sage Code",
                editable: true,
                flex: 1
            },
            {
                field: "precinctApproval",
                headerName: "Precinct Approval",
                renderCell: (params) => GetFieldLookupItem(lookups, 'PrecinctApproval', params.row.precinctApproval)?.value?.toString(),
                editable: true,
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <DataGridSelect
                        {...params?? {}}
                        lookups={lookups ?? []}
                        value={params.value ? params.value : ""} 
                    />
                ),
                flex: 1
            }
        ];

        return columns;
    }, [lookups]);

    return projectPrecinctColumns;
}

export const BulkEditProjectPrecincts: FC = () => {
    const snackbar = useSnackbar();

    const projectPrecinctMetadata= useFormMetadata(FormDefinitions.ProjectPrecinct);

    const save = useFormSave(projectPrecinctMetadata);

    const theme = useTheme();
    
    const params = useParams();
    
    const projectId = Number(params.projectId);
        
    const {data: projectPrecincts, loading: projectPrecinctsLoading, refresh: precinctsRefresh } = useProjectPrecinctsByProjectId(projectId);


    const submit = useCallback(async (newPrecicnt: ProjectPrecinct, oldPrecicnt: ProjectPrecinct) => {
        await save(newPrecicnt)
            .catch(reason => {
                    reason.responseStatus.errors.map((error: any) => 
                        snackbar.enqueueSnackbar(
                            error.message, 
                            { variant: "error-critical" }
                        )
                    )
                    newPrecicnt = oldPrecicnt;
                }
            );

        precinctsRefresh();
        return newPrecicnt;
    }, [save, precinctsRefresh, snackbar]);

    const {data: project} = useContext(ProjectContext);
    
    const customToolbarProps = useMemo(() => createCustomToolbarProps({
        exportTitle: `${project?.name} - Precincts`,
        fileName: `${project?.name} - Precincts`,
        includeDateTime: true
    }), [project]);   
    
    const columns = useProjectPrecinctColumns();
    
    const addNewPrecinct = useCallback(async () => {
        let precinct = new ProjectPrecinct(
            {
                projectId: projectId,
            }
        );
        await save(precinct);
        await precinctsRefresh();
        return precinct;
    }, [precinctsRefresh, save]);

    return (

        //Properties

        //Summary

        //StageList
        <>
            <Outlet />

            <TitleBox
                display={"flex"}
                justifyContent={"flex-end"}
            >
                <StyledButton 
                    variant="contained"
                    onClick={addNewPrecinct}
                >
                    Add Precinct
                </StyledButton>
            </TitleBox>

            <StyledPaper>
                <Box
                    sx={{
                        [`& .${gridClasses.root}`]: {
                            
                            [`& .${gridClasses.columnHeader}`]: {
                                height: 'auto !important',
                                minHeight: 56,
                                
                                [`& .${gridClasses.columnHeaderTitleContainer}`]: {
                                    whiteSpace: 'normal',
                                    
                                    [`& .${gridClasses.columnHeaderTitle}`]: {
                                        whiteSpace: 'normal',
                                        paddingTop: theme.spacing(1.5),
                                        paddingBottom: theme.spacing(1.5) 
                                    }
                                },
                                
                                [`& .${gridClasses.iconButtonContainer}`]: {
                                    width: 'unset',
                                },
                                
                                '&.column-group-header': {
                                    backgroundColor: theme.palette.grey[300],
                                    boxShadow: "1",
                                    textAlign: 'center'
                                }
                            }
                        },
                    }}
                    >
                    <CollapsibleTablePremium
                        density="compact"
                        editMode="row"
                        processRowUpdate={(newRow, oldRow, _params) => submit(newRow, oldRow)}
                        //onRowEditStop={(params, event) => lotEditSubmit(params, event)}
                        startCollapsed={false} 
                        title={"Project Precincts"} 
                        loading={projectPrecinctsLoading} 
                        columns={columns}
                        rows={projectPrecincts}
                        disableColumnFilter={false}
                        {...customToolbarProps}
                    />
                </Box>
            </StyledPaper>
        </>
    );
}